import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { Message } from '../../conversations/types/Conversation';
import {
  removeOptimisticMessages,
  removeRedundantOptimisticMessages,
  replaceOptimisticMessages,
  stripRedundantOptimisticMessages,
} from '../../conversations/utils/optimisticMessages';
import { WebWidgetClient, createWebWidgetClient } from '../services/webWidgetClient';
import {
  WebWidgetRealtimeStatus,
  connectWebWidgetSessionEvents,
} from '../services/webWidgetSessionEvents';
import {
  createWebWidgetOptimisticAttachmentMessage,
  createWebWidgetOptimisticTextMessage,
} from '../utils/webWidgetOptimisticMessages';
import {
  WebWidgetAppearance,
  WebWidgetBootstrapResponse,
  WebWidgetConversationState,
  WebWidgetSessionAttachmentMessageRequest,
  WebWidgetSessionMessageRequest,
  WebWidgetTypingIndicator,
  WebWidgetTypingParticipantType,
  WebWidgetVisitorIdentitySettings,
  WebWidgetVisitorState,
} from '../types/webWidget';
import {
  filterActiveTypingIndicators,
  pruneTypingIndicatorsAfterMessages,
} from '../utils/webWidgetTypingIndicators';

type WebWidgetSessionStatus = 'idle' | 'initializing' | 'ready' | 'refreshing' | 'revoking' | 'error';
type WebWidgetSyncStatus = 'idle' | 'live' | 'degraded';

interface WebWidgetSessionState {
  status: WebWidgetSessionStatus;
  error: string | null;
  tenantId: string | null;
  tenantCode: string | null;
  tenantName: string | null;
  widgetId: string | null;
  widgetName: string | null;
  welcomeTitle?: string;
  welcomeMessage?: string;
  appearance: WebWidgetAppearance;
  visitorIdentity: WebWidgetVisitorIdentitySettings;
  sessionId: string | null;
  sessionExpiresAtUtc: string | null;
  visitor: WebWidgetVisitorState | null;
  conversation: WebWidgetConversationState | null;
  messages: Message[];
  loadedCount: number;
  totalCount: number;
  hasMoreMessages: boolean;
  lastSyncedAt: string | null;
  syncStatus: WebWidgetSyncStatus;
  pollFailureCount: number;
  nextPollAt: string | null;
  typingIndicators: WebWidgetTypingIndicator[];
  realtimeStatus: WebWidgetRealtimeStatus;
}

interface HydratedSessionPayload {
  bootstrap: WebWidgetBootstrapResponse | null;
  sessionId: string;
  sessionExpiresAtUtc: string;
  widgetId: string;
  widgetName: string;
  appearance: WebWidgetAppearance;
  visitorIdentity: WebWidgetVisitorIdentitySettings;
  visitor: WebWidgetVisitorState;
  conversation: WebWidgetConversationState | null;
  messages: Message[];
  loadedCount: number;
  totalCount: number;
  hasMoreMessages: boolean;
  lastSyncedAt: string;
  typingIndicators: WebWidgetTypingIndicator[];
}

const ACTIVE_POLLING_INTERVAL_MS = 2500;
const ACTIVE_POLLING_WINDOW_MS = 30_000;
const REALTIME_FALLBACK_POLLING_INTERVAL_MS = 60_000;

const mapSenderToTypingParticipantType = (message: Message): WebWidgetTypingParticipantType | null => {
  if (message.sender.type === 'companyMember') {
    return 'companyMember';
  }

  if (message.sender.type === 'aiAssistant') {
    return 'aiAssistant';
  }

  return null;
};

const pruneTypingAfterIncomingMessages = (
  indicators: WebWidgetTypingIndicator[],
  messages: Message[],
): WebWidgetTypingIndicator[] => {
  const participantTypes = messages.flatMap(message => {
    const participantType = mapSenderToTypingParticipantType(message);
    return participantType ? [participantType] : [];
  });

  return filterActiveTypingIndicators(
    pruneTypingIndicatorsAfterMessages(indicators, participantTypes),
  );
};

type Action =
  | { type: 'operationStarted'; status: Extract<WebWidgetSessionStatus, 'initializing' | 'refreshing' | 'revoking'> }
  | { type: 'sessionHydrated'; payload: HydratedSessionPayload }
  | { type: 'sessionPolled'; payload: Omit<HydratedSessionPayload, 'bootstrap'> }
  | { type: 'messagesAppended'; payload: { messages: Message[]; loadedCount: number; totalCount: number; hasMoreMessages: boolean; lastSyncedAt: string } }
  | { type: 'messagesCommitted'; payload: { messages: Message[]; lastSyncedAt: string } }
  | { type: 'messagesReconciled'; payload: { replacements: Array<{ optimisticId: string; message: Message }>; visitor?: WebWidgetVisitorState | null; conversation?: WebWidgetConversationState | null; lastSyncedAt: string } }
  | { type: 'messagesRemoved'; payload: { messageIds: string[]; error?: string | null } }
  | { type: 'messageAdded'; payload: { message: Message; visitor?: WebWidgetVisitorState | null; conversation?: WebWidgetConversationState | null; lastSyncedAt: string } }
  | { type: 'sessionUpdated'; payload: { sessionId: string; sessionExpiresAtUtc: string; lastSyncedAt: string } }
  | { type: 'pollFailed'; payload: { pollFailureCount: number; nextPollAt: string } }
  | { type: 'realtimeStatusChanged'; payload: { status: WebWidgetRealtimeStatus } }
  | { type: 'realtimeTypingUpdated'; payload: { typingIndicators: WebWidgetTypingIndicator[]; lastSyncedAt: string } }
  | { type: 'realtimeMessageCreated'; payload: { message: Message; lastSyncedAt: string } }
  | { type: 'reset' }
  | { type: 'failed'; message: string };

const initialState: WebWidgetSessionState = {
  status: 'idle',
  error: null,
  tenantId: null,
  tenantCode: null,
  tenantName: null,
  widgetId: null,
  widgetName: null,
  welcomeTitle: undefined,
  welcomeMessage: undefined,
  appearance: {},
  visitorIdentity: {
    requireEmail: false,
    requirePhone: false,
  },
  sessionId: null,
  sessionExpiresAtUtc: null,
  visitor: null,
  conversation: null,
  messages: [],
  loadedCount: 0,
  totalCount: 0,
  hasMoreMessages: false,
  lastSyncedAt: null,
  syncStatus: 'idle',
  pollFailureCount: 0,
  nextPollAt: null,
  typingIndicators: [],
  realtimeStatus: 'idle',
};

const getNextPollDelayMs = (pollingIntervalMs: number, pollFailureCount: number) => {
  const multiplier = Math.min(Math.max(2 ** Math.max(pollFailureCount, 0), 1), 8);
  return pollingIntervalMs * multiplier;
};

const LEGACY_SESSION_STORAGE_PREFIX = 'webWidgetSession';

const purgeLegacyWidgetSessionStorage = (tenantCode: string, widgetKey: string): void => {
  try {
    sessionStorage.removeItem(`${LEGACY_SESSION_STORAGE_PREFIX}:${tenantCode.trim().toLowerCase()}:${widgetKey.trim().toLowerCase()}`);
  } catch {
    // Ignore.
  }
};

const mergeMessages = (existing: Message[], incoming: Message[]): Message[] => {
  let merged = existing;

  incoming.forEach(message => {
    merged = removeRedundantOptimisticMessages(merged, message);
  });

  const byId = new Map<string, Message>();

  merged.forEach(message => {
    byId.set(message.id, message);
  });

  incoming.forEach(message => {
    byId.set(message.id, message);
  });

  return stripRedundantOptimisticMessages(
    Array.from(byId.values()).sort((left, right) => (
      new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime()
    )),
  );
};

const reducer = (state: WebWidgetSessionState, action: Action): WebWidgetSessionState => {
  switch (action.type) {
    case 'operationStarted':
      return {
        ...state,
        status: action.status,
        error: null,
      };
    case 'sessionHydrated': {
      const { bootstrap, ...payload } = action.payload;
      return {
        ...state,
        status: 'ready',
        error: null,
        tenantId: bootstrap?.tenantId ?? state.tenantId,
        tenantCode: bootstrap?.tenantCode ?? state.tenantCode,
        tenantName: bootstrap?.tenantName ?? state.tenantName,
        widgetId: payload.widgetId,
        widgetName: payload.widgetName,
        welcomeTitle: bootstrap?.welcomeTitle ?? state.welcomeTitle,
        welcomeMessage: bootstrap?.welcomeMessage ?? state.welcomeMessage,
        appearance: payload.appearance,
        visitorIdentity: bootstrap?.visitorIdentity ?? payload.visitorIdentity ?? state.visitorIdentity ?? { requireEmail: false, requirePhone: false },
        sessionId: payload.sessionId,
        sessionExpiresAtUtc: payload.sessionExpiresAtUtc,
        visitor: payload.visitor,
        conversation: payload.conversation,
        messages: payload.messages,
        loadedCount: payload.loadedCount,
        totalCount: payload.totalCount,
        hasMoreMessages: payload.hasMoreMessages,
        lastSyncedAt: payload.lastSyncedAt,
        typingIndicators: filterActiveTypingIndicators(payload.typingIndicators),
        syncStatus: payload.conversation ? 'live' : 'idle',
        pollFailureCount: 0,
        nextPollAt: null,
      };
    }
    case 'messagesAppended': {
      const previousMessageIds = new Set(state.messages.map(message => message.id));
      const newMessages = action.payload.messages.filter(message => !previousMessageIds.has(message.id));

      return {
        ...state,
        status: 'ready',
        error: null,
        messages: mergeMessages(state.messages, action.payload.messages),
        loadedCount: action.payload.loadedCount,
        totalCount: action.payload.totalCount,
        hasMoreMessages: action.payload.hasMoreMessages,
        lastSyncedAt: action.payload.lastSyncedAt,
        typingIndicators: pruneTypingAfterIncomingMessages(state.typingIndicators, newMessages),
        syncStatus: state.conversation ? 'live' : state.syncStatus,
        pollFailureCount: 0,
        nextPollAt: null,
      };
    }
    case 'sessionPolled': {
      const previousMessageIds = new Set(state.messages.map(message => message.id));
      const newMessages = action.payload.messages.filter(message => !previousMessageIds.has(message.id));

      return {
        ...state,
        status: 'ready',
        error: null,
        widgetId: action.payload.widgetId,
        widgetName: action.payload.widgetName,
        appearance: action.payload.appearance,
        visitorIdentity: action.payload.visitorIdentity ?? { requireEmail: false, requirePhone: false },
        sessionId: action.payload.sessionId,
        sessionExpiresAtUtc: action.payload.sessionExpiresAtUtc,
        visitor: action.payload.visitor,
        conversation: action.payload.conversation,
        messages: mergeMessages(state.messages, action.payload.messages),
        loadedCount: Math.max(state.loadedCount, action.payload.loadedCount),
        totalCount: Math.max(state.totalCount, action.payload.totalCount),
        hasMoreMessages: action.payload.hasMoreMessages || Math.max(state.loadedCount, action.payload.loadedCount) < Math.max(state.totalCount, action.payload.totalCount),
        lastSyncedAt: action.payload.lastSyncedAt,
        typingIndicators: pruneTypingAfterIncomingMessages(
          filterActiveTypingIndicators(action.payload.typingIndicators),
          newMessages,
        ),
        syncStatus: action.payload.conversation ? 'live' : 'idle',
        pollFailureCount: 0,
        nextPollAt: null,
      };
    }
    case 'messagesCommitted': {
      const newMessages = action.payload.messages.filter(
        message => !state.messages.some(item => item.id === message.id),
      );

      return {
        ...state,
        status: 'ready',
        error: null,
        messages: mergeMessages(state.messages, action.payload.messages),
        loadedCount: state.loadedCount + newMessages.length,
        totalCount: state.totalCount + newMessages.length,
        lastSyncedAt: action.payload.lastSyncedAt,
      };
    }
    case 'messagesReconciled':
      return {
        ...state,
        status: 'ready',
        error: null,
        visitor: action.payload.visitor === undefined ? state.visitor : action.payload.visitor,
        conversation: action.payload.conversation === undefined ? state.conversation : action.payload.conversation,
        messages: stripRedundantOptimisticMessages(replaceOptimisticMessages(state.messages, action.payload.replacements)),
        lastSyncedAt: action.payload.lastSyncedAt,
        syncStatus: state.conversation || action.payload.conversation ? 'live' : state.syncStatus,
      };
    case 'realtimeStatusChanged':
      return {
        ...state,
        realtimeStatus: action.payload.status,
      };
    case 'realtimeTypingUpdated':
      return {
        ...state,
        typingIndicators: filterActiveTypingIndicators(action.payload.typingIndicators),
        lastSyncedAt: action.payload.lastSyncedAt,
        syncStatus: state.conversation ? 'live' : state.syncStatus,
      };
    case 'realtimeMessageCreated': {
      const alreadyPresent = state.messages.some(item => item.id === action.payload.message.id);
      const newMessages = alreadyPresent ? [] : [action.payload.message];

      return {
        ...state,
        messages: mergeMessages(state.messages, [action.payload.message]),
        loadedCount: alreadyPresent ? state.loadedCount : state.loadedCount + 1,
        totalCount: alreadyPresent ? state.totalCount : state.totalCount + 1,
        lastSyncedAt: action.payload.lastSyncedAt,
        typingIndicators: pruneTypingAfterIncomingMessages(state.typingIndicators, newMessages),
        syncStatus: state.conversation ? 'live' : state.syncStatus,
      };
    }
    case 'messagesRemoved': {
      const removedIds = new Set(action.payload.messageIds.map(id => id.trim().toLowerCase()));
      const removedCount = state.messages.filter(message => removedIds.has(message.id.trim().toLowerCase())).length;

      return {
        ...state,
        status: 'ready',
        error: action.payload.error ?? null,
        messages: removeOptimisticMessages(state.messages, action.payload.messageIds),
        loadedCount: Math.max(0, state.loadedCount - removedCount),
        totalCount: Math.max(0, state.totalCount - removedCount),
      };
    }
    case 'messageAdded':
      return {
        ...state,
        status: 'ready',
        error: null,
        visitor: action.payload.visitor === undefined ? state.visitor : action.payload.visitor,
        conversation: action.payload.conversation === undefined ? state.conversation : action.payload.conversation,
        messages: mergeMessages(state.messages, [action.payload.message]),
        loadedCount: Math.max(state.loadedCount, state.messages.some(item => item.id === action.payload.message.id) ? state.loadedCount : state.loadedCount + 1),
        totalCount: Math.max(state.totalCount, state.messages.some(item => item.id === action.payload.message.id) ? state.totalCount : state.totalCount + 1),
        lastSyncedAt: action.payload.lastSyncedAt,
        syncStatus: state.conversation || action.payload.conversation ? 'live' : state.syncStatus,
      };
    case 'sessionUpdated':
      return {
        ...state,
        status: 'ready',
        error: null,
        sessionId: action.payload.sessionId,
        sessionExpiresAtUtc: action.payload.sessionExpiresAtUtc,
        lastSyncedAt: action.payload.lastSyncedAt,
      };
    case 'pollFailed':
      return {
        ...state,
        syncStatus: 'degraded',
        pollFailureCount: action.payload.pollFailureCount,
        nextPollAt: action.payload.nextPollAt,
      };
    case 'reset':
      return {
        ...initialState,
        tenantId: state.tenantId,
        tenantCode: state.tenantCode,
        tenantName: state.tenantName,
      };
    case 'failed':
      return {
        ...state,
        status: 'error',
        error: action.message,
      };
    default:
      return state;
  }
};

export interface WebWidgetSessionContextValue extends WebWidgetSessionState {
  isReady: boolean;
  hasSession: boolean;
  visibleTypingIndicators: WebWidgetTypingIndicator[];
  initialize: () => Promise<void>;
  recover: () => Promise<void>;
  loadMoreMessages: () => Promise<void>;
  sendMessage: (request: WebWidgetSessionMessageRequest) => Promise<void>;
  sendAttachment: (request: WebWidgetSessionAttachmentMessageRequest) => Promise<void>;
  refreshSession: () => Promise<void>;
  revokeSession: () => Promise<void>;
  reset: () => void;
}

interface WebWidgetSessionProviderProps {
  children: React.ReactNode;
  tenantCode: string;
  widgetKey: string;
  client?: WebWidgetClient;
  initialMessagePageSize?: number;
  pollingIntervalMs?: number;
  origin?: string;
}

const WebWidgetSessionContext = createContext<WebWidgetSessionContextValue | undefined>(undefined);

const toErrorMessage = (error: unknown): string => {
  return error instanceof Error && error.message
    ? error.message
    : 'Unable to complete widget session request.';
};

const createSyncTimestamp = () => new Date().toISOString();

export const WebWidgetSessionProvider: React.FC<WebWidgetSessionProviderProps> = ({
  children,
  tenantCode,
  widgetKey,
  client: clientOverride,
  initialMessagePageSize = 50,
  pollingIntervalMs = 15000,
  origin,
}) => {
  const pollInFlightRef = useRef(false);
  const activePollUntilRef = useRef(0);
  const client = useMemo(
    () => clientOverride ?? createWebWidgetClient({
      tenantCode,
      widgetKey,
      origin: origin ?? (typeof window !== 'undefined' ? window.location.origin : undefined),
    }),
    [clientOverride, origin, tenantCode, widgetKey],
  );

  const [state, dispatch] = useReducer(
    reducer,
    { tenantCode, widgetKey },
    ({ tenantCode: initialTenantCode }) => {
      purgeLegacyWidgetSessionStorage(initialTenantCode, widgetKey);

      return {
        ...initialState,
        tenantCode: initialTenantCode,
      };
    },
  );

  const typingIndicatorsRef = useRef(state.typingIndicators);
  const nextPollAtRef = useRef(state.nextPollAt);
  const realtimeStatusRef = useRef(state.realtimeStatus);
  typingIndicatorsRef.current = state.typingIndicators;
  nextPollAtRef.current = state.nextPollAt;
  realtimeStatusRef.current = state.realtimeStatus;

  const extendActivePolling = useCallback(() => {
    activePollUntilRef.current = Date.now() + ACTIVE_POLLING_WINDOW_MS;
  }, []);

  const hydrateSession = useCallback(async (bootstrap: WebWidgetBootstrapResponse | null) => {
    const lastSyncedAt = createSyncTimestamp();
    const sessionState = await client.getSessionState();
    const sessionMessages = sessionState.conversation
      ? await client.getSessionMessages({ loadedCount: 0, pageSize: initialMessagePageSize })
      : {
        conversationId: null,
        messages: [],
        loadedCount: 0,
        totalCount: 0,
        hasMoreMessages: false,
      };

    dispatch({
      type: 'sessionHydrated',
      payload: {
        bootstrap,
        sessionId: sessionState.sessionId,
        sessionExpiresAtUtc: sessionState.expiresAtUtc,
        widgetId: sessionState.widgetId,
        widgetName: sessionState.widgetName,
        appearance: sessionState.appearance,
        visitorIdentity: bootstrap?.visitorIdentity ?? sessionState.visitorIdentity ?? { requireEmail: false, requirePhone: false },
        visitor: sessionState.visitor,
        conversation: sessionState.conversation,
        messages: sessionMessages.messages,
        loadedCount: sessionMessages.loadedCount,
        totalCount: sessionMessages.totalCount,
        hasMoreMessages: sessionMessages.hasMoreMessages,
        lastSyncedAt,
        typingIndicators: sessionState.typingIndicators ?? [],
      },
    });
  }, [client, initialMessagePageSize]);

  const initialize = useCallback(async () => {
    dispatch({ type: 'operationStarted', status: 'initializing' });

    try {
      const bootstrap = await client.bootstrap({ tenantCode, widgetKey });
      await hydrateSession(bootstrap);
    } catch (error) {
      dispatch({ type: 'failed', message: toErrorMessage(error) });
      throw error;
    }
  }, [client, hydrateSession, tenantCode, widgetKey]);

  const recover = useCallback(async () => {
    dispatch({ type: 'operationStarted', status: 'initializing' });

    try {
      await hydrateSession(null);
    } catch {
      client.setSessionToken(null);
      try {
        await initialize();
      } catch {
        // initialize already records the failure in session state.
      }
    }
  }, [client, hydrateSession, initialize]);

  const loadMoreMessages = useCallback(async () => {
    if (!state.sessionId || !state.conversation || !state.hasMoreMessages) {
      return;
    }

    dispatch({ type: 'operationStarted', status: 'initializing' });

    try {
      const page = await client.getSessionMessages({
        loadedCount: state.loadedCount,
        pageSize: initialMessagePageSize,
      });

      dispatch({
        type: 'messagesAppended',
        payload: {
          messages: page.messages,
          loadedCount: page.loadedCount,
          totalCount: page.totalCount,
          hasMoreMessages: page.hasMoreMessages,
          lastSyncedAt: createSyncTimestamp(),
        },
      });
    } catch (error) {
      dispatch({ type: 'failed', message: toErrorMessage(error) });
      throw error;
    }
  }, [client, initialMessagePageSize, state.conversation, state.hasMoreMessages, state.loadedCount, state.sessionId]);

  const syncSessionMessagesAfterOutbound = useCallback(async (
    replacements: Array<{ optimisticId: string; message: Message }>,
    request?: Pick<WebWidgetSessionMessageRequest, 'displayName' | 'externalVisitorId'>,
  ) => {
    const shouldRehydrate = !state.conversation
      || (request?.displayName !== undefined && request.displayName !== state.visitor?.displayName)
      || (request?.externalVisitorId !== undefined && request.externalVisitorId !== state.visitor?.externalVisitorId);

    dispatch({
      type: 'messagesReconciled',
      payload: {
        replacements,
        lastSyncedAt: createSyncTimestamp(),
        visitor: state.visitor
          ? {
            ...state.visitor,
            displayName: request?.displayName ?? state.visitor.displayName,
            externalVisitorId: request?.externalVisitorId ?? state.visitor.externalVisitorId,
          }
          : state.visitor,
      },
    });

    const pageSize = Math.min(Math.max(state.messages.length + 1, initialMessagePageSize), 200);
    const sessionMessages = await client.getSessionMessages({
      loadedCount: 0,
      pageSize,
    });

    dispatch({
      type: 'messagesAppended',
      payload: {
        messages: sessionMessages.messages,
        loadedCount: sessionMessages.loadedCount,
        totalCount: sessionMessages.totalCount,
        hasMoreMessages: sessionMessages.hasMoreMessages,
        lastSyncedAt: createSyncTimestamp(),
      },
    });

    if (shouldRehydrate) {
      await hydrateSession(null);
    }
  }, [client, hydrateSession, initialMessagePageSize, state.conversation, state.messages.length, state.visitor]);

  const sendMessage = useCallback(async (request: WebWidgetSessionMessageRequest) => {
    if (!state.sessionId) {
      throw new Error('Widget session has not been initialized.');
    }

    const trimmedContent = request.content.trim();
    if (!trimmedContent) {
      throw new Error('Message content is required.');
    }

    const optimisticMessage = createWebWidgetOptimisticTextMessage({
      conversationId: state.conversation?.id ?? '',
      visitor: state.visitor,
      content: trimmedContent,
      displayName: request.displayName,
    });

    dispatch({
      type: 'messagesCommitted',
      payload: {
        messages: [optimisticMessage],
        lastSyncedAt: createSyncTimestamp(),
      },
    });

    extendActivePolling();

    try {
      const response = await client.sendSessionMessage(request);
      await syncSessionMessagesAfterOutbound(
        [{ optimisticId: optimisticMessage.id, message: response.message }],
        request,
      );
    } catch (error) {
      dispatch({
        type: 'messagesRemoved',
        payload: {
          messageIds: [optimisticMessage.id],
          error: toErrorMessage(error),
        },
      });
      throw error;
    }
  }, [client, extendActivePolling, state.conversation?.id, state.sessionId, state.visitor, syncSessionMessagesAfterOutbound]);

  const sendAttachment = useCallback(async (request: WebWidgetSessionAttachmentMessageRequest) => {
    if (!state.sessionId) {
      throw new Error('Widget session has not been initialized.');
    }

    const optimisticMessage = createWebWidgetOptimisticAttachmentMessage({
      conversationId: state.conversation?.id ?? '',
      visitor: state.visitor,
      content: request.content?.trim() ?? '',
      file: request.file,
      displayName: request.displayName,
    });

    dispatch({
      type: 'messagesCommitted',
      payload: {
        messages: [optimisticMessage],
        lastSyncedAt: createSyncTimestamp(),
      },
    });

    extendActivePolling();

    try {
      const response = await client.sendSessionAttachment(request);
      await syncSessionMessagesAfterOutbound(
        [{ optimisticId: optimisticMessage.id, message: response.message }],
        request,
      );
    } catch (error) {
      dispatch({
        type: 'messagesRemoved',
        payload: {
          messageIds: [optimisticMessage.id],
          error: toErrorMessage(error),
        },
      });
      throw error;
    }
  }, [client, extendActivePolling, state.conversation?.id, state.sessionId, state.visitor, syncSessionMessagesAfterOutbound]);

  const refreshSession = useCallback(async () => {
    if (!state.sessionId) {
      throw new Error('Widget session has not been initialized.');
    }

    dispatch({ type: 'operationStarted', status: 'refreshing' });

    try {
      const response = await client.refreshSession();
      dispatch({
        type: 'sessionUpdated',
        payload: {
          sessionId: response.session.id,
          sessionExpiresAtUtc: response.session.expiresAtUtc,
          lastSyncedAt: createSyncTimestamp(),
        },
      });
    } catch (error) {
      dispatch({ type: 'failed', message: toErrorMessage(error) });
      throw error;
    }
  }, [client, state.sessionId]);

  const revokeSession = useCallback(async () => {
    if (!state.sessionId) {
      dispatch({ type: 'reset' });
      return;
    }

    dispatch({ type: 'operationStarted', status: 'revoking' });

    try {
      await client.revokeSession();
      dispatch({ type: 'reset' });
    } catch (error) {
      dispatch({ type: 'failed', message: toErrorMessage(error) });
      throw error;
    }
  }, [client, state.sessionId]);

  const reset = useCallback(() => {
    dispatch({ type: 'reset' });
  }, []);

  useEffect(() => {
    if (!state.sessionId) {
      return undefined;
    }

    const resolvedOrigin = origin ?? (typeof window !== 'undefined' ? window.location.origin : undefined);
    const connection = connectWebWidgetSessionEvents({
      tenantCode,
      widgetKey,
      origin: resolvedOrigin,
      getSessionToken: () => client.getSessionToken(),
    }, {
      onStatusChange: (status) => {
        dispatch({ type: 'realtimeStatusChanged', payload: { status } });
      },
      onTypingUpdated: (typingIndicators) => {
        dispatch({
          type: 'realtimeTypingUpdated',
          payload: {
            typingIndicators,
            lastSyncedAt: createSyncTimestamp(),
          },
        });
      },
      onMessageCreated: (message) => {
        dispatch({
          type: 'realtimeMessageCreated',
          payload: {
            message,
            lastSyncedAt: createSyncTimestamp(),
          },
        });
      },
    });

    return () => {
      connection.close();
    };
  }, [client, origin, state.conversation?.id, state.sessionId, tenantCode, widgetKey]);

  const pollSession = useCallback(async () => {
    const now = Date.now();

    if (
      pollInFlightRef.current
      || !state.sessionId
      || !state.conversation
      || state.status === 'initializing'
      || state.status === 'refreshing'
      || state.status === 'revoking'
      || (nextPollAtRef.current ? new Date(nextPollAtRef.current).getTime() > now : false)
    ) {
      return;
    }

    pollInFlightRef.current = true;

    try {
      const lastSyncedAt = createSyncTimestamp();
      const sessionState = await client.getSessionState();
      const pageSize = Math.min(Math.max(state.messages.length, initialMessagePageSize), 200);
      const sessionMessages = sessionState.conversation
        ? await client.getSessionMessages({ loadedCount: 0, pageSize })
        : {
          conversationId: null,
          messages: [],
          loadedCount: 0,
          totalCount: 0,
          hasMoreMessages: false,
        };

      dispatch({
        type: 'sessionPolled',
        payload: {
          sessionId: sessionState.sessionId,
          sessionExpiresAtUtc: sessionState.expiresAtUtc,
          widgetId: sessionState.widgetId,
          widgetName: sessionState.widgetName,
          appearance: sessionState.appearance,
          visitorIdentity: sessionState.visitorIdentity ?? { requireEmail: false, requirePhone: false },
          visitor: sessionState.visitor,
          conversation: sessionState.conversation,
          messages: sessionMessages.messages,
          loadedCount: sessionMessages.loadedCount,
          totalCount: sessionMessages.totalCount,
          hasMoreMessages: sessionMessages.hasMoreMessages,
          lastSyncedAt,
          typingIndicators: sessionState.typingIndicators ?? [],
        },
      });
    } catch {
      const pollFailureCount = state.pollFailureCount + 1;
      const nextPollAt = new Date(now + getNextPollDelayMs(pollingIntervalMs, pollFailureCount)).toISOString();

      dispatch({
        type: 'pollFailed',
        payload: {
          pollFailureCount,
          nextPollAt,
        },
      });
    } finally {
      pollInFlightRef.current = false;
    }
  }, [client, initialMessagePageSize, pollingIntervalMs, state.conversation, state.messages.length, state.pollFailureCount, state.sessionId, state.status]);

  useEffect(() => {
    if (!state.sessionId || !state.conversation || pollingIntervalMs <= 0) {
      return;
    }

    let timeoutId = 0;

    const scheduleNextPoll = () => {
      const isRealtimeConnected = realtimeStatusRef.current === 'connected';
      const shouldUseActivePolling = !isRealtimeConnected && (
        Date.now() < activePollUntilRef.current
        || filterActiveTypingIndicators(typingIndicatorsRef.current).length > 0
      );
      const delayMs = shouldUseActivePolling
        ? ACTIVE_POLLING_INTERVAL_MS
        : (isRealtimeConnected ? REALTIME_FALLBACK_POLLING_INTERVAL_MS : pollingIntervalMs);

      timeoutId = window.setTimeout(() => {
        void pollSession().finally(scheduleNextPoll);
      }, delayMs);
    };

    scheduleNextPoll();

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [pollSession, pollingIntervalMs, state.conversation, state.sessionId]);

  const visibleTypingIndicators = useMemo(
    () => filterActiveTypingIndicators(state.typingIndicators),
    [state.typingIndicators],
  );

  const value = useMemo<WebWidgetSessionContextValue>(() => ({
    ...state,
    visibleTypingIndicators,
    isReady: state.status === 'ready',
    hasSession: Boolean(state.sessionId),
    initialize,
    recover,
    loadMoreMessages,
    sendMessage,
    sendAttachment,
    refreshSession,
    revokeSession,
    reset,
  }), [
    initialize,
    loadMoreMessages,
    recover,
    refreshSession,
    reset,
    revokeSession,
    sendAttachment,
    sendMessage,
    state,
    visibleTypingIndicators,
  ]);

  return <WebWidgetSessionContext.Provider value={value}>{children}</WebWidgetSessionContext.Provider>;
};

export const useWebWidgetSession = () => {
  const context = useContext(WebWidgetSessionContext);
  if (!context) {
    throw new Error('useWebWidgetSession must be used within a WebWidgetSessionProvider');
  }

  return context;
};